import { CSI } from './SGR';

/**
 * Post-processing passes over an ANSI stream.
 *
 * The encoder already emits minimal state transitions, so this exists for two
 * other cases: streams that came from somewhere else (imported files, hand
 * written output), and the merge opportunities that only appear once the stream
 * is flat — for example two adjacent SGR sequences with no text between them.
 *
 * Every pass is required to preserve the rendered result exactly.
 */

export interface OptimizeStats {
  rawBytes: number;
  optimizedBytes: number;
  savedBytes: number;
  savedRatio: number;
  mergedSequences: number;
  removedSequences: number;
}

export interface OptimizeResult {
  text: string;
  stats: OptimizeStats;
}

const SGR_RE = /\u001b\[([0-9;:]*)m/y;

interface Chunk {
  type: 'sgr' | 'text' | 'other';
  value: string;
  /** SGR parameters, for sgr chunks. */
  params?: string;
}

/** Splits a stream into SGR sequences, other escape sequences, and text. */
export function tokenizeStream(input: string): Chunk[] {
  const out: Chunk[] = [];
  let i = 0;
  let text = '';
  const flush = () => {
    if (text) {
      out.push({ type: 'text', value: text });
      text = '';
    }
  };

  while (i < input.length) {
    if (input[i] === '\u001b') {
      SGR_RE.lastIndex = i;
      const m = SGR_RE.exec(input);
      if (m) {
        flush();
        out.push({ type: 'sgr', value: m[0], params: m[1] });
        i += m[0].length;
        continue;
      }
      // Any other escape sequence: copy it through untouched.
      const end = findEscapeEnd(input, i);
      flush();
      out.push({ type: 'other', value: input.slice(i, end) });
      i = end;
      continue;
    }
    text += input[i];
    i += 1;
  }
  flush();
  return out;
}

function findEscapeEnd(input: string, start: number): number {
  let i = start + 1;
  if (i >= input.length) return input.length;
  const c = input[i];
  if (c === '[') {
    i += 1;
    while (i < input.length) {
      const code = input.charCodeAt(i);
      if (code >= 0x40 && code <= 0x7e) return i + 1;
      i += 1;
    }
    return input.length;
  }
  if (c === ']' || c === 'P' || c === '^' || c === '_') {
    i += 1;
    while (i < input.length) {
      if (input[i] === '\u0007') return i + 1;
      if (input[i] === '\u001b' && input[i + 1] === '\\') return i + 2;
      i += 1;
    }
    return input.length;
  }
  return i + 1;
}

/** Normalises SGR params: drops empty entries, collapses a leading reset. */
function normalizeParams(params: string): string[] {
  const parts = params.split(';').map((p) => p.trim());
  const out: string[] = [];
  for (const p of parts) {
    if (p === '' || p === '0') {
      // A reset invalidates everything before it in the same sequence.
      out.length = 0;
      out.push('0');
      continue;
    }
    out.push(p);
  }
  if (out.length > 1 && out[0] === '0') return out;
  return out;
}

/**
 * Merges runs of consecutive SGR sequences and removes sequences that are
 * immediately superseded, e.g. `ESC[31m ESC[32m` with no text in between.
 */
export function optimizeAnsi(input: string): OptimizeResult {
  const rawBytes = new TextEncoder().encode(input).length;
  const chunks = tokenizeStream(input);
  const out: string[] = [];
  let merged = 0;
  let removed = 0;

  let pendingParams: string[] | null = null;

  const flushPending = () => {
    if (!pendingParams) return;
    if (pendingParams.length === 0) {
      removed += 1;
      pendingParams = null;
      return;
    }
    out.push(`${CSI}${pendingParams.join(';')}m`);
    pendingParams = null;
  };

  for (const chunk of chunks) {
    if (chunk.type === 'sgr') {
      const params = normalizeParams(chunk.params ?? '');
      if (pendingParams === null) {
        pendingParams = params;
      } else {
        merged += 1;
        if (params[0] === '0') {
          pendingParams = params;
        } else {
          pendingParams = dedupe([...pendingParams, ...params]);
        }
      }
      continue;
    }
    if (chunk.type === 'text' && chunk.value.length === 0) continue;
    flushPending();
    out.push(chunk.value);
  }
  // A trailing SGR with nothing after it still matters (terminals keep state),
  // so it is emitted rather than dropped.
  flushPending();

  const text = out.join('');
  const optimizedBytes = new TextEncoder().encode(text).length;
  return {
    text,
    stats: {
      rawBytes,
      optimizedBytes,
      savedBytes: rawBytes - optimizedBytes,
      savedRatio: rawBytes > 0 ? (rawBytes - optimizedBytes) / rawBytes : 0,
      mergedSequences: merged,
      removedSequences: removed,
    },
  };
}

/**
 * Later parameters win over earlier ones in the same category, so a merged
 * sequence keeps only the last foreground, background and each attribute.
 */
function dedupe(params: string[]): string[] {
  const attrs = new Map<string, string>();
  let fg: string[] | null = null;
  let bg: string[] | null = null;
  let hasReset = false;
  const order: string[] = [];

  for (let i = 0; i < params.length; i++) {
    const p = params[i];
    const n = Number.parseInt(p, 10);
    if (p === '0') {
      hasReset = true;
      attrs.clear();
      fg = null;
      bg = null;
      order.length = 0;
      continue;
    }
    if (n === 38 || n === 48) {
      const kind = Number.parseInt(params[i + 1] ?? '', 10);
      const take = kind === 5 ? 3 : kind === 2 ? 5 : 1;
      const seq = params.slice(i, i + take);
      if (n === 38) fg = seq;
      else bg = seq;
      i += take - 1;
      continue;
    }
    if ((n >= 30 && n <= 39) || (n >= 90 && n <= 97)) {
      fg = [p];
      continue;
    }
    if ((n >= 40 && n <= 49) || (n >= 100 && n <= 107)) {
      bg = [p];
      continue;
    }
    const key = attrKey(n);
    if (!attrs.has(key)) order.push(key);
    attrs.set(key, p);
  }

  const out: string[] = [];
  if (hasReset) out.push('0');
  for (const key of order) {
    const v = attrs.get(key);
    if (v !== undefined) out.push(v);
  }
  if (fg) out.push(...fg);
  if (bg) out.push(...bg);
  return out;
}

/** Groups an attribute code with the code that disables it. */
function attrKey(code: number): string {
  switch (code) {
    case 1:
    case 2:
    case 22:
      return 'weight';
    case 3:
    case 23:
      return 'italic';
    case 4:
    case 24:
      return 'underline';
    case 5:
    case 6:
    case 25:
      return 'blink';
    case 7:
    case 27:
      return 'inverse';
    case 8:
    case 28:
      return 'hidden';
    case 9:
    case 29:
      return 'strike';
    default:
      return `c${code}`;
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export interface BandwidthEstimate {
  frameBytes: number;
  fps: number;
  bytesPerSecond: number;
  warning: string | null;
}

/** Terminal throughput estimate for animated projects. */
export function estimateBandwidth(frameBytes: number, fps: number): BandwidthEstimate {
  const bps = frameBytes * fps;
  let warning: string | null = null;
  if (bps > 1_000_000) {
    warning = 'Over 1 MB/s — will stutter badly over SSH. Reduce FPS or use differential frames.';
  } else if (bps > 250_000) {
    warning = 'Over 250 KB/s — noticeable lag on slow links. Consider differential frames.';
  } else if (bps > 100_000) {
    warning = 'Over 100 KB/s — fine locally, sluggish on constrained connections.';
  }
  return { frameBytes, fps, bytesPerSecond: bps, warning };
}
